/**
 * Avatar initials + deterministic background color.
 *
 * Per Batch 30b Q30b-6 ruling + web-designer §9: split the candidate name on
 * whitespace, take the first letter of each word, slice to 2, uppercase.
 *
 * The 10-color palette is recovered verbatim from the prototype's ``av()``
 * helper (commit ``a85e660``). Variety humanizes the avatar grid — we
 * deliberately don't constrain to brand teal/blue here (web-designer §9
 * ratification). 30c/30d/30e all consume this helper unchanged.
 */

import { AVATAR_COLORS } from "./tokens";
export { AVATAR_COLORS };

/**
 * Compute the 1–2 character upper-cased initials for a candidate name.
 *
 * Examples:
 *   - "Jane Doe"          → "JD"
 *   - "Madonna"           → "M"
 *   - "Mary Jane Watson"  → "MJ"  (slice 2)
 *   - ""    / null / undef → "?"   (last-resort fallback; server enforces non-empty)
 */
export function initials(name: string | null | undefined): string {
  if (!name || !name.trim()) return "?";
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Deterministic palette pick from the candidate name. Same name → same color
 * across renders (so a given candidate's avatar is stable in the recruiter's
 * eye).
 *
 * Hash is a small djb2-style fold; cheap, no external dep.
 */
export function avatarColor(name: string | null | undefined): string {
  if (!name) return AVATAR_COLORS[0]!;
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]!;
}

/**
 * Slightly darker shade of a hex color for the gradient bottom-right corner.
 *
 * @param hex     ``#RRGGBB``
 * @param percent  Negative (e.g. -15) to darken; positive to lighten.
 */
export function shade(hex: string, percent: number): string {
  const m = hex.match(/^#?([\da-f]{6})$/i);
  if (!m || !m[1]) return hex;
  const num = parseInt(m[1], 16);
  const amt = Math.round(2.55 * percent);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amt));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
